// Common/Interceptors/AxiosConfig.js
import axios from "axios";
import { clearUserFromStorage } from "../../utils/storage";
import {
  isLocalToRemote,
  getAccessToken,
  getRefreshToken,
  clearTokens,
  storeTokens
} from "../../utils/authHelper";
import logger from "../../utils/logger";

const BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Plain axios instance (no interceptors)
 * Used for auth endpoints that don't need token refresh
 */
export const plainAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: !isLocalToRemote(),
});

/**
 * ✅ CRITICAL: plainAxios token-delivery interceptor
 *
 * Problem: Login/OTP-verify requests from TWA have NO body-mode signals:
 *   - No Bearer token (user isn't logged in yet)
 *   - No refreshToken in body (this IS the login, not a refresh)
 *   - After first nav, origin = https://bookizap.com (same as browser)
 *
 * Without this header, backend falls through to isProductionWebOrigin()
 * → returns 'cookie' → sets httpOnly cookies → returns NO tokens in body
 * → storeTokens() never runs → bkz_rt never saved to localStorage
 * → next refresh: getRefreshToken() = null → redirect to /login ❌
 *
 * With this header: backend hits Priority 2 → returns 'body' → tokens
 * come back in response → storeTokens() saves bkz_rt to localStorage ✅
 */
plainAxios.interceptors.request.use(
  (config) => {
    if (isLocalToRemote()) {
      config.headers["x-token-delivery"] = "body";
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Main axios instance with interceptors
 * Automatically adds auth headers and handles token refresh
 */
export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: !isLocalToRemote(),
});

// Request interceptor - Add Authorization header
// ✅ FIX: Always add Authorization header when we have a memory token,
// even in cookie mode. This is "belt + suspenders" — the cookie is the
// primary auth mechanism but the Authorization header acts as a fallback
// when the browser hasn't processed the Set-Cookie yet.
axiosInstance.interceptors.request.use(
  (config) => {
    logger.log("📤 Axios Request:", {
      url: config.url,
      method: config.method,
      authMode: isLocalToRemote() ? 'token' : 'cookie',
    });

    if (isLocalToRemote()) {
      // ✅ Always signal body-mode to backend for token-based environments
      config.headers["x-token-delivery"] = "body";
    }

    // ✅ FIX: Always add Authorization header if we have an access token in memory
    // In cookie mode this acts as a safety net for the Set-Cookie race condition
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    logger.error("❌ Request setup error:", error);
    return Promise.reject(error);
  }
);

// Token refresh management
let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null; // ✅ Singleton promise to prevent concurrent refreshes

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Internal logic for refreshing access token.
 * DO NOT call this directly; use refreshAccessToken() instead to benefit from the singleton lock.
 */
const performRefresh = async () => {
  logger.log("🔄 Executing token refresh...");
  const refreshUrl = window.location.hostname.includes("admin")
    ? (localStorage.getItem('superadmin_isAuthenticated') ? "/super-admin/auth/refresh" : "/admin/auth/refresh")
    : "/users/auth/refresh";

  try {
    if (isLocalToRemote()) {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token available');

      const response = await plainAxios.post(
        refreshUrl,
        { refreshToken },
        { 
          headers: { 'x-token-delivery': 'body' },
          withCredentials: false // ❌ FIX: Do not send credentials in body-mode to prevent old cookie conflicts in TWA
        }
      );

      if (response?.data?.success || response?.data?.status === "success") {
        const data = response.data.data || {};
        const tokensData = data.tokens || data;
        const at = tokensData?.accessToken;
        const rt = tokensData?.refreshToken || tokensData?.bkz_rt;

        if (at) {
          storeTokens(at, rt);
          logger.log("✅ Token refreshed (localStorage)");
          return true;
        }
      }
    } else {
      const response = await plainAxios.post(refreshUrl, {}, { withCredentials: true });
      if (response?.data?.success || response?.data?.status === "success") {
        const data = response.data.data || {};
        const bodyAccessToken = data.accessToken;
        if (bodyAccessToken) storeTokens(bodyAccessToken, null);
        logger.log("✅ Token refreshed (cookies)");
        return true;
      }
    }
    throw new Error("Token refresh rejected by server");
  } catch (error) {
    logger.error("❌ Token refresh failed:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Singleton Refresh Handler
 * Ensures that multiple concurrent 401s only trigger ONE refresh call.
 */
export const refreshAccessToken = async () => {
  if (refreshPromise) {
    logger.log("⏳ Waiting for existing refresh to complete...");
    return refreshPromise;
  }

  refreshPromise = performRefresh();
  
  try {
    const result = await refreshPromise;
    return result;
  } finally {
    refreshPromise = null; // ✅ Release lock
  }
};

/**
 * Handle authentication failure
 * Clears tokens and redirects to login
 */
const handleAuthFailure = (error, url) => {
  // ✅ FIX: ONLY log out if the server explicitly rejects the token (401/403/400).
  // If it's a network error (no response), we should NOT kick the user out, UNLESS it's a missing token error.
  if (!error.response && error.message !== 'No refresh token available') {
    logger.warn("🌐 Network error during refresh. Keeping session alive.", url);
    return;
  }

  const status = error.response.status;
  if (status !== 401 && status !== 403 && status !== 400 && status !== 404) {
    logger.warn(`⚠️ Non-auth error (${status}) during refresh. Skipping logout.`, url);
    return;
  }

  logger.log("🚪 Authentication failed permanently for:", url);

  // ✅ Hard-Clear everything to stop loops
  clearTokens();
  clearUserFromStorage();
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("user");
  localStorage.removeItem("serviceProviderEnabled");
  // ❌ Do NOT remove twa_mode flag. If they are in a TWA, they remain in a TWA even after logout!
  
  setTimeout(() => {
    const PUBLIC_EXACT_ROUTES = new Set([
      "/", "/login", "/register", "/otp", "/forgot-password",
      "/privacy-policy", "/terms-conditions", "/contact",
      "/refund-cancellation", "/ourServices", "/aboutUs",
    ]);

    const PUBLIC_PREFIX_ROUTES = [
      "/service-details/", "/services/service-group/", "/service-invitation/",
      "/master-service/", "/explore", "/s/", "/profile/", "/w/",
    ];

    const currentPath = window.location?.pathname || "/";
    const isPublic = PUBLIC_EXACT_ROUTES.has(currentPath) || 
                   PUBLIC_PREFIX_ROUTES.some((p) => currentPath.startsWith(p)) ||
                   /^\/[a-f0-9]{24}\/master-services/.test(currentPath);

    if (!isPublic && currentPath !== "/login") {
      window.dispatchEvent(new CustomEvent('BZ_AUTH_FAILURE'));
    }
  }, 100);
};

// Response interceptor - Handle errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    logger.log("✅ Response successful:", response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    logger.error("❌ Response error:", {
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.response?.data?.message,
    });

    // Network error
    if (!error.response) {

      return Promise.reject(error);
    }

    const status = error.response.status;

    // Handle 401 (Unauthorized) - Token expired
    if (status === 401 && !originalRequest._retry) {
      logger.log("🔐 Got 401 - Token Expired");

      // Don't retry for auth endpoints
      if (
        originalRequest.url.includes("/users/auth/login") ||
        originalRequest.url.includes("/users/auth/register") ||
        originalRequest.url.includes("/users/auth/google") ||
        originalRequest.url.includes("/users/auth/refresh") ||
        originalRequest.url.includes("/users/auth/logout") ||
        originalRequest.url.includes("/auth/") // legacy fallback
      ) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await refreshAccessToken();
        
        // ✅ FIX: Reset isRefreshing BEFORE processing queue.
        // This ensures retries can behave normally.
        isRefreshing = false;
        processQueue(null);

        // ✅ FIX: Always add Authorization header for retry,
        // regardless of auth mode (belt + suspenders)
        const newAccessToken = getAccessToken();
        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError);
        handleAuthFailure(refreshError, originalRequest.url);
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 (Forbidden)
    if (status === 403) {
      /* 
      showNotification({
        message: "You don't have permission to access this resource.",
        type: "error",
        theme: "light",
      });
      */
    }

    // Handle server errors (5xx)
    if (status >= 500) {
      /*
      showNotification({
        message: "Server error. Please try again later.",
        type: "error",
        theme: "light",
      });
      */
    }

    // Handle other client errors (4xx)
    // ✅ DIAGNOSTIC LOGGING for "id unified" error
    const errorMsg = error.response?.data?.message || "";
    const originalUrl = error.config?.url || "";
    if (errorMsg.includes("unified") || originalUrl.includes("unified")) {
      
    }

    if (status >= 400 && status < 500 && status !== 401 && status !== 403) {
      if (!originalRequest.skipNotification) {
        /*
        showNotification({
          message: error.response?.data?.message || "Request failed. Please try again.",
          type: "error",
          theme: "light",
        });
        */
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Clear authentication cookies/tokens and logout
 */
export const clearAuthCookies = async () => {
  try {
    // Call logout endpoint
    await plainAxios.post("/users/auth/logout", {}, {
      withCredentials: !isLocalToRemote()
    });

    // Clear local storage/tokens
    clearTokens();
    clearUserFromStorage();

    logger.log("✅ Auth cleared successfully");
  } catch (error) {
    logger.error("❌ Error clearing auth:", error);

    // Clear tokens even if API call fails
    clearTokens();
    clearUserFromStorage();
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  if (isLocalToRemote()) {
    // Token-based: Check localStorage
    return !!(getAccessToken() && getRefreshToken());
  }
  // Cookie-based: Assume authenticated (backend validates)
  return true;
};

export { isLocalToRemote };
